import { useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2Icon, InfoIcon, SparklesIcon } from "lucide-react";

import useGqlClient from "@/src/hooks/useGqlClient";
import {
  QuestionClustersQuery,
  QuestionClusterStatus,
  useQuestionClustersQuery,
} from "@/src/graphql/generated";
import clsx from "clsx";
import { ClusterModal } from "./ClusterModal";
import { ProgressBar } from "@/src/components/ProgressBar";

type Cluster = QuestionClustersQuery["questionClusters"][0];
type ClusterMember = Cluster["members"][0];

export function QuestionClusters() {
  const { dealId } = useParams<{ dealId: string }>();
  const client = useGqlClient();
  const [selectedClusterId, setSelectedClusterId] = useState<string | null>(null);

  const clustersQuery = useQuestionClustersQuery(client, {
    input: {
      dealID: dealId,
    },
  });

  const clusters = clustersQuery.data?.questionClusters ?? [];


  return (
    <div className="p-4 max-h-[calc(100vh-200px)] overflow-y-scroll ">

      {clustersQuery.isLoading ? (
        <ClusterListLoading />
      ) : clustersQuery.error ? (
        <div className="p-6 text-center">
          <p className="text-persian-990 text-sm">
            Something went wrong loading clusters.
          </p>
        </div>
      ) : clusters.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-2">
          {clusters.sort((a, b) => {

            if (a.status === QuestionClusterStatus.Dismissed && b.status === QuestionClusterStatus.New) {
              return 1;
            }

            if (a.status === QuestionClusterStatus.New && b.status === QuestionClusterStatus.Dismissed) {
              return -1;
            }

            const aAllAnswered = a.members.every((m) => m.question.answeredAt);
            const bAllAnswered = b.members.every((m) => m.question.answeredAt);

            if (aAllAnswered && !bAllAnswered) {
              return 1;
            }
            if (!aAllAnswered && bAllAnswered) {
              return -1;
            }

            if (a.members.length > b.members.length) {
              return -1
            }

            if (b.members.length > a.members.length) {
              return 1
            }

            return 0;
          }).map((cluster) => (
            <ClusterCard
              key={cluster.id}
              cluster={cluster}
              onAnswer={() => setSelectedClusterId(cluster.id)}
            />
          ))}
        </div>
      )}

      <ClusterModal
        open={!!selectedClusterId}
        clusterId={selectedClusterId ?? undefined}
        onClose={() => {
          setSelectedClusterId(null);
          clustersQuery.refetch();
        }}
      />
    </div>
  );
}

function ClusterCard(props: {
  cluster: Cluster;
  onAnswer: () => void;
}) {
  const { cluster } = props;
  const answeredQuestions = cluster.members.filter((m) => m.question.answeredAt).length

  return (
    <div className={clsx("border border-concrete-100 rounded-lg bg-white shadow-sm flex flex-col",
      cluster.status === QuestionClusterStatus.Dismissed ? "opacity-70 hover:opacity-100" : ""
    )}>
      <div className="flex items-start justify-between p-4 pb-3">
        <div className="flex flex-1 items-center gap-x-2">
          <div className="w-40">
            <ProgressBar height="h-1.5" progress={(answeredQuestions / cluster.members.length) * 100} />
          </div>
          <p className="text-xs font-medium text-concrete-500 ">{answeredQuestions}/{cluster.members.length} answered</p>


          {cluster.status === QuestionClusterStatus.Dismissed ? (
            <div className="bg-concrete-50 border-concrete-200 text-xs font-medium rounded-full px-2 py-0.5 border">
              <p>Dismissed</p>
            </div>
          ) : (

            <SimilarityBadge members={cluster.members} />
          )}
        </div>
        <button
          onClick={props.onAnswer}
          className="p-1 rounded-md hover:bg-persian-50 active:bg-persian-100 font-semibold text-persian-900 hover:text-persian-800 text-sm"
        >
          Answer group
        </button>
      </div>

      <div className="px-4 pb-4 flex-1 flex flex-col gap-y-2 max-h-80 overflow-y-scroll">
        {cluster.members.map((member) => (
          <ClusterMemberRow key={member.id} member={member} />
        ))}
      </div>
    </div>
  );
}

function SimilarityBadge(props: { members: ClusterMember[] }) {
  if (props.members.length < 2) return null;
  const avgScore =
    props.members.reduce((sum, m) => sum + m.similarityScore, 0) /
    props.members.length;
  const pct = Math.round(avgScore * 100);

  return (
    <span
      className={clsx(
        "text-xs font-medium rounded-full px-2 py-0.5 border",
        pct >= 80
          ? "bg-green-50 border-green-200 text-green-700"
          : pct >= 60
            ? "bg-yellow-50 border-yellow-200 text-yellow-700"
            : "bg-concrete-50 border-concrete-200 text-concrete-600",
      )}
    >
      {pct}% similar
    </span>
  );
}

function QuestionBreadcrumbs({
  question,
}: {
  question: QuestionClustersQuery['questionClusters'][0]['members'][0]['question'];
}) {
  const { dealId } = useParams<{ dealId: string }>();

  let nodes: string[] = [];

  if (question.ddql) {
    question.ddql.parentDdqls.forEach((ddql) => {
      if (ddql.name === "") {
        return;
      }
      nodes.push(ddql.name);
    });

    nodes.push(question.ddql.name);
  }

  return (
    <div>
      <div className="flex items-center gap-x-1.5">
        {nodes.map((breadcrumb, index) => (
          <div key={index} className="flex items-center gap-x-1.5">
            <p className="text-xs text-concrete-600">{breadcrumb}</p>
            <p className="font-light text-concrete-600 text-xs">/</p>
          </div>
        ))}
      </div>
    </div>
  );
}


function ClusterMemberRow(props: { member: ClusterMember }) {
  const { member } = props;
  return (
    <div className="p-2 hover:bg-concrete-50 border border-concrete-100 rounded-md">
      <div className="min-w-0 flex justify-between items-center gap-x-2">
        <div>
          <QuestionBreadcrumbs question={member.question} />
          <p className="text-xs font-semibold text-concrete-950 line-clamp-2">
            {member.question.title}
          </p>
        </div>
        {member.question.answeredAt ? (
          <CheckCircle2Icon className="flex-shrink-0 text-green-500 w-4 h-4" />
        ) : (
          <CheckCircle2Icon className="flex-shrink-0 text-concrete-200 w-4 h-4" />
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <SparklesIcon className="w-12 h-12 text-concrete-300 mb-3" />
      <p className="text-base font-semibold text-concrete-700">
        No question groups to review
      </p>
      <p className="text-sm text-concrete-500 mt-1">
        Liquid hasn&apos;t detected any similar question groups yet.
      </p>
    </div>
  );
}

function ClusterListLoading() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="border border-concrete-100 rounded-lg bg-white shadow-sm p-4 animate-pulse"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="h-5 bg-gray-200 rounded-full w-20" />
            <div className="h-7 bg-gray-200 rounded w-16" />
          </div>
          <div className="space-y-3">
            <div className="flex items-start gap-x-2">
              <div className="w-5 h-5 bg-gray-200 rounded-full shrink-0" />
              <div className="flex-1 space-y-1">
                <div className="h-4 bg-gray-200 rounded w-full" />
                <div className="h-3 bg-gray-200 rounded w-2/3" />
              </div>
            </div>
            <div className="flex items-start gap-x-2">
              <div className="w-5 h-5 bg-gray-200 rounded-full shrink-0" />
              <div className="flex-1 space-y-1">
                <div className="h-4 bg-gray-200 rounded w-5/6" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ClustersBanner(props: { dealId: string }) {
  const client = useGqlClient();
  const [modalOpen, setModalOpen] = useState(false);

  const clustersQuery = useQuestionClustersQuery(client, {
    input: { dealID: props.dealId, unansweredOnly: true },
  });

  const newClusters = (clustersQuery.data?.questionClusters ?? []).filter(
    (c) => c.status === QuestionClusterStatus.New,
  );

  if (clustersQuery.isLoading || clustersQuery.error || newClusters.length === 0) {
    return null;
  }

  return (
    <div className="px-2 pb-2">
      <div className="bg-persian-50 border border-persian-200 p-2 rounded-md flex items-center justify-between">
        <div className="flex items-center gap-x-2">
          <InfoIcon className="w-4 h-4 text-persian-800" />
          <p className="text-persian-900 text-sm">
            Liquid found {newClusters.length} similar question{" "}
            {newClusters.length === 1 ? "group" : "groups"}
          </p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="font-semibold text-persian-900 hover:text-persian-800 text-sm"
        >
          Start answering
        </button>
      </div>
      <ClusterModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          clustersQuery.refetch();
        }}
      />
    </div>
  );
}
